import RichText from 'components/UI/Richtext';
import { ReactElement, useContext } from 'react';
import {
  IComponentVideoPlayerFields,
  IFragmentContentfulHostedVideo,
  IFragmentRendiaVideo,
  IFragmentVimeoVideo,
  IFragmentYouTubeVideo,
  IFragmentFreshpaintVideo,
} from 'types/contentful';
import { useRouter } from 'next/router';
import SiteDataContext from 'context/SiteDataContext';
import Vimeo, { isVimeoVideoGuard } from 'components/UI/Videos/Vimeo';
import FreshpaintVideo, { isFreshpaintVideoGuard } from 'components/UI/Videos/Freshpaint';
import YouTube, { isYoutubeVideoGuard } from 'components/UI/Videos/Youtube';
import Rendia, { isRendiaVideoGuard } from 'components/UI/Videos/Rendia';
import ContentfulVideo from 'components/UI/Videos/ContentfulVideo';

export enum VideoTypeId {
  Vimeo = 'fragmentVimeoVideo',
  Youtube = 'fragmentYouTubeVideo',
  Contentful = 'fragmentContentfulHostedVideo',
  Rendia = 'fragmentRendiaVideo',
  Freshpaint = 'fragmentFreshpaintVideo',
}

export type VideoTypes =
  | IFragmentContentfulHostedVideo
  | IFragmentVimeoVideo
  | IFragmentYouTubeVideo
  | IFragmentRendiaVideo
  | IFragmentFreshpaintVideo;

function fullUrl(domain: string, path: string): URL {
  const protocol = domain.startsWith('localhost') ? 'http' : 'https';

  return new URL(`${protocol}://${domain}${path}`);
}

interface VideoPlayerProps extends IComponentVideoPlayerFields {
  className?: string | undefined;
}

export default function VideoPlayer({
  heading,
  video,
  backgroundColor,
  description,
  descriptionAlignment = 'Center',
  className,
}: VideoPlayerProps): ReactElement {
  const router = useRouter();
  const { domain } = useContext(SiteDataContext);

  const Comp = (): ReactElement => {
    if (isVimeoVideoGuard(video)) {
      return <Vimeo video={video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube video={video} />;
    } else if (isRendiaVideoGuard(video)) {
      return <Rendia video={video} url={fullUrl(domain, router.asPath)} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return <FreshpaintVideo video={video} />;
    } else {
      return <ContentfulVideo video={video} />;
    }
  };

  const alignmentClass =
    descriptionAlignment === 'Left'
      ? 'text-left'
      : descriptionAlignment === 'Right'
      ? 'text-right'
      : 'text-center';

  return (
    <div className="ecp-my">
      <div
        className={`px-6 ${
          backgroundColor === 'white' ? 'pb-10 md:pb-20' : 'py-10 md:py-20'
        } bg-${backgroundColor} text-primary-actual space-y-1`}
      >
        <div className={`bg-transparent text-center`}>
          {heading && (
            <h2 className="font-heading text-new-34 lg:text-4xl lg:leading-new-43 font-light">
              {heading}
            </h2>
          )}
          {description && (
            <div className={`px-4 lg:px-32 mt-4 ${alignmentClass}`}>
              <RichText document={description} classNames="text-lg" />
            </div>
          )}
        </div>
        <div className={`text-center`}>
          <div className={`inline-block mt-5 px-4 w-full ${className ? '' : 'lg:w-3/4'}`}>
            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <Comp />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
